<script setup lang="ts">
import { computed, onMounted, watchEffect } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import AccountCalendars from '@/components/accountSettings/AccountCalendars.vue'
import AccountDetail from '@/components/accountSettings/AccountDetail.vue'
import AccountForm from '@/components/accountSettings/AccountForm.vue'
import AccountList from '@/components/accountSettings/AccountList.vue'
import CalendarDetail from '@/components/accountSettings/CalendarDetail.vue'
import CalendarSharing from '@/components/accountSettings/CalendarSharing.vue'
import { useAccountsEditor } from '@/composables/useAccountsEditor'
import { accountsPath, parseAccountsPath } from '@/settings/accountsNav'

// The accounts category of the Settings (`/settings/accounts[/new|<id>[/edit|calendars[/<calendar>[/sharing]]]]`): picks the page
// from the route and prints that page's own description above it. The accounts are read when the category opens.
const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const editor = useAccountsEditor()
const view = computed(() => parseAccountsPath(route.params.rest))
const account = computed(() => {
  const v = view.value
  return 'id' in v ? editor.accounts.value.find((a) => a.id === v.id) : undefined
})
const calendar = computed(() => {
  const v = view.value
  return account.value && 'calendarId' in v
    ? editor.calendarsByAccount[account.value.id]?.find((c) => c.id === v.calendarId)
    : undefined
})

watchEffect(() => {
  if (view.value.view === 'unknown') void router.replace(accountsPath())
  // (an account that does not exist, e.g. after deleting it or a stale link, goes back to the list)
  else if ('id' in view.value && editor.loaded.value && !account.value) void router.replace(accountsPath())
})

// The calendars are read live per account: opening a page below one needs them (a link straight to a calendar included).
watchEffect(() => {
  if (account.value && (view.value.view === 'calendars' || 'calendarId' in view.value)) {
    void editor.loadCalendars(account.value)
  }
})
watchEffect(() => {
  if (account.value && calendar.value && view.value.view === 'sharing') void editor.loadGrants(calendar.value)
})

onMounted(() => editor.load())
</script>

<template>
  <p v-if="view.view !== 'unknown'" class="ui-hint page-hint">{{ t(`accounts.pageHint.${view.view}`) }}</p>
  <p v-if="editor.loadError.value" class="error">{{ editor.loadError.value }}</p>
  <p v-else-if="editor.loading.value && !editor.loaded.value">{{ t('common.loading') }}</p>
  <template v-else>
    <AccountList v-if="view.view === 'list'" />
    <AccountForm v-else-if="view.view === 'new'" />
    <template v-else-if="account">
      <AccountDetail v-if="view.view === 'account'" :account="account" />
      <AccountForm v-else-if="view.view === 'edit'" :account="account" />
      <AccountCalendars v-else-if="view.view === 'calendars'" :account="account" />
      <template v-else-if="calendar">
        <CalendarDetail v-if="view.view === 'calendar'" :account="account" :calendar="calendar" />
        <CalendarSharing v-else-if="view.view === 'sharing'" :account="account" :calendar="calendar" />
      </template>
      <p v-else-if="editor.calendarsError[account.id]" class="error">{{ editor.calendarsError[account.id] }}</p>
      <p v-else>{{ t('common.loading') }}</p>
    </template>
  </template>
</template>

<style scoped>
.page-hint {
  margin: 0 0.25rem 0.75rem;
}

.error {
  color: var(--tg-destructive-text-color);
}
</style>
